import { collection, deleteDoc, doc, setDoc } from 'firebase/firestore';
import { deleteObject, getBlob, ref, uploadBytes } from 'firebase/storage';
import { db, storage } from '../firebase';
import type { DiscussionAttachment } from './types';

export const MAX_ATTACHMENT_SIZE_BYTES = 10 * 1024 * 1024;
export const ALLOWED_ATTACHMENT_TYPES = ['image/png', 'image/jpeg', 'application/pdf'] as const;

const ALLOWED_EXTENSIONS = ['.png', '.jpg', '.jpeg', '.pdf'];

export type AttachmentTarget =
  | { kind: 'topic'; topicId: string }
  | { kind: 'reply'; topicId: string; replyId: string };

export function isAllowedAttachmentFile(file: File) {
  const name = file.name.toLowerCase();
  return (
    ALLOWED_ATTACHMENT_TYPES.includes(file.type as any) &&
    ALLOWED_EXTENSIONS.some((extension) => name.endsWith(extension))
  );
}

export function validateAttachmentFiles(files: File[]) {
  for (const file of files) {
    if (!isAllowedAttachmentFile(file)) {
      return `${file.name} is not a supported file type. Use PNG, JPG/JPEG, or PDF.`;
    }
    if (file.size > MAX_ATTACHMENT_SIZE_BYTES) {
      return `${file.name} is too large. Attachments must be 10 MB or smaller.`;
    }
  }
  return '';
}

function safeFileName(fileName: string) {
  const cleaned = fileName
    .trim()
    .replace(/[/\\?%*:|"<>]/g, '-')
    .replace(/\s+/g, ' ')
    .slice(0, 140);
  return cleaned || 'attachment';
}

function attachmentCollectionRef(target: AttachmentTarget) {
  if (target.kind === 'topic') return collection(db, 'discussionTopics', target.topicId, 'attachments');
  return collection(db, 'discussionTopics', target.topicId, 'replies', target.replyId, 'attachments');
}

function attachmentDocRef(attachment: DiscussionAttachment) {
  if (attachment.replyId) {
    return doc(
      db,
      'discussionTopics',
      attachment.topicId,
      'replies',
      attachment.replyId,
      'attachments',
      attachment.id
    );
  }
  return doc(db, 'discussionTopics', attachment.topicId, 'attachments', attachment.id);
}

function attachmentStoragePath(target: AttachmentTarget, attachmentId: string, fileName: string) {
  if (target.kind === 'topic') {
    return `discussionAttachments/${target.topicId}/topic/${attachmentId}/${safeFileName(fileName)}`;
  }
  return `discussionAttachments/${target.topicId}/replies/${target.replyId}/${attachmentId}/${safeFileName(fileName)}`;
}

export async function uploadDiscussionAttachments(input: {
  target: AttachmentTarget;
  ownerProfileId: string;
  files: File[];
}) {
  const error = validateAttachmentFiles(input.files);
  if (error) throw new Error(error);

  for (const file of input.files) {
    const attachmentRef = doc(attachmentCollectionRef(input.target));
    const storagePath = attachmentStoragePath(input.target, attachmentRef.id, file.name);
    const storageRef = ref(storage, storagePath);
    const createdAt = Date.now();

    await uploadBytes(storageRef, file, {
      contentType: file.type,
      customMetadata: {
        ownerProfileId: input.ownerProfileId,
        topicId: input.target.topicId,
        ...(input.target.kind === 'reply' ? { replyId: input.target.replyId } : {})
      }
    });

    const metadata: Omit<DiscussionAttachment, 'id'> = {
      topicId: input.target.topicId,
      ...(input.target.kind === 'reply' ? { replyId: input.target.replyId } : {}),
      ownerProfileId: input.ownerProfileId,
      fileName: file.name,
      contentType: file.type as DiscussionAttachment['contentType'],
      sizeBytes: file.size,
      storagePath,
      createdAt
    };

    await setDoc(attachmentRef, metadata);
  }
}

export async function deleteDiscussionAttachment(attachment: DiscussionAttachment) {
  try {
    await deleteObject(ref(storage, attachment.storagePath));
  } catch (err: any) {
    if (err?.code !== 'storage/object-not-found') throw err;
  }
  await deleteDoc(attachmentDocRef(attachment));
}

export function getDiscussionAttachmentUrl(attachment: DiscussionAttachment) {
  return getBlob(ref(storage, attachment.storagePath), MAX_ATTACHMENT_SIZE_BYTES).then((blob) => URL.createObjectURL(blob));
}

function encodePathSegment(value: string) {
  return encodeURIComponent(value);
}

export function getDiscussionAttachmentOpenUrl(attachment: DiscussionAttachment) {
  const topicId = encodePathSegment(attachment.topicId);
  const attachmentId = encodePathSegment(attachment.id);
  if (attachment.replyId) {
    return `/attachments/discussion/${topicId}/replies/${encodePathSegment(attachment.replyId)}/${attachmentId}`;
  }
  return `/attachments/discussion/${topicId}/topic/${attachmentId}`;
}

export function formatAttachmentSize(sizeBytes: number) {
  if (sizeBytes >= 1024 * 1024) return `${(sizeBytes / 1024 / 1024).toFixed(1)} MB`;
  if (sizeBytes >= 1024) return `${Math.ceil(sizeBytes / 1024)} KB`;
  return `${sizeBytes} B`;
}
